import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CachedIcon from "@mui/icons-material/Cached";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import DownloadIcon from "@mui/icons-material/Download";
import { useTheme } from "@mui/material/styles";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import {
  getBacktestData,
  getAvailableSymbols,
  getIndexPresets,
  getIndexSymbols,
  getBacktestHistory,
  getBacktestHistoryItem,
  deleteBacktestRun,
  renameBacktestRun,
} from "../Service/data";

const STRATEGY_OPTIONS = [
  { value: "simple_breakout", label: "Simple Breakout" },
  { value: "strong_breakout", label: "Strong Breakout" },
  { value: "pullback", label: "Pullback" },
  { value: "ROC_Buy_Signal", label: "ROC Buy Signal" },
  { value: "ROC_Sell_Signal", label: "ROC Sell Signal" },
  { value: "TradeRoom_Buy", label: "TradeRoom Buy (EMA 9/15)" },
  { value: "TradeRoom_Sell", label: "TradeRoom Sell (EMA 9/15)" },
];

const COLORS = {
  green: "#2e7d32",
  red: "#d32f2f",
  blue: "#1976d2",
  orange: "#ed6c02",
};

const formatCurrency = (v) => {
  if (v === null || v === undefined) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(v);
};

// Map backend strategy names to Pine Script keys
function strategyToPineKey(name) {
  const lower = (name || "").toLowerCase();
  if (lower.includes("simple") && lower.includes("breakout")) return "simple_breakout";
  if (lower.includes("strong") && lower.includes("breakout")) return "strong_breakout";
  if (lower.includes("pullback")) return "pullback";
  if (lower.includes("roc") && lower.includes("buy")) return "ROC_Buy_Signal";
  if (lower.includes("roc") && lower.includes("sell")) return "ROC_Sell_Signal";
  if (lower.includes("traderoom") && lower.includes("buy")) return "TradeRoom_Buy";
  if (lower.includes("traderoom") && lower.includes("sell")) return "TradeRoom_Sell";
  return name;
}

function generatePineScript(strategyName, config) {
  const tp = config?.take_profit ?? 10;
  const sl = config?.stop_loss ?? 5;
  const maxHold = config?.max_hold_days ?? 20;

  const strategies = {
    simple_breakout: {
      title: "Simple Breakout Strategy",
      body: `// Simple Breakout: Price breaks above highest high of consolidation period
lookback = input.int(20, "Lookback Period")
highestHigh = ta.highest(high, lookback)
lowestLow = ta.lowest(low, lookback)
range_pct = (highestHigh - lowestLow) / lowestLow * 100
consolidating = range_pct < 10
breakout = close > highestHigh[1] and consolidating[1]
if breakout
    strategy.entry("Long", strategy.long)`,
    },
    strong_breakout: {
      title: "Strong Breakout Strategy",
      body: `// Strong Breakout: Breakout + Volume surge + DMI confirmation
lookback = input.int(20, "Lookback Period")
volMa = ta.sma(volume, 20)
[diPlus, diMinus, adx] = ta.dmi(14, 14)
highestHigh = ta.highest(high, lookback)
lowestLow = ta.lowest(low, lookback)
range_pct = (highestHigh - lowestLow) / lowestLow * 100
consolidating = range_pct < 10
volSurge = volume > volMa * 1.5
breakout = close > highestHigh[1] and consolidating[1] and volSurge and diPlus > diMinus
if breakout
    strategy.entry("Long", strategy.long)`,
    },
    pullback: {
      title: "Pullback Strategy",
      body: `// Pullback: Recent high + ROC z-score dip + bullish candle
lookback_high = input.int(60, "High Lookback")
roc_period = input.int(20, "ROC Period")
roc = ta.roc(close, roc_period)
roc_mean = ta.sma(roc, 50)
roc_std = ta.stdev(roc, 50)
roc_z = (roc - roc_mean) / roc_std
made_high = ta.highest(high, lookback_high) == ta.highest(high, 10)
bullish = close > open and (close - low) > (high - close)
pullback = made_high and roc_z < -1.8 and bullish
if pullback
    strategy.entry("Long", strategy.long)`,
    },
    ROC_Buy_Signal: {
      title: "ROC Buy Signal Strategy",
      body: `// ROC Buy: ROC z-score < -1.8 + bullish candlestick pattern
roc_period = input.int(20, "ROC Period")
roc = ta.roc(close, roc_period)
roc_mean = ta.sma(roc, 50)
roc_std = ta.stdev(roc, 50)
roc_z = (roc - roc_mean) / roc_std
bullish = close > open
body_pct = math.abs(close - open) / (high - low)
roc_buy = roc_z < -1.8 and bullish and body_pct > 0.4
if roc_buy
    strategy.entry("Long", strategy.long)`,
    },
    ROC_Sell_Signal: {
      title: "ROC Sell Signal Strategy",
      body: `// ROC Sell: ROC z-score > 1.8 + bearish candlestick pattern
roc_period = input.int(20, "ROC Period")
roc = ta.roc(close, roc_period)
roc_mean = ta.sma(roc, 50)
roc_std = ta.stdev(roc, 50)
roc_z = (roc - roc_mean) / roc_std
bearish = close < open
body_pct = math.abs(close - open) / (high - low)
roc_sell = roc_z > 1.8 and bearish and body_pct > 0.4
if roc_sell
    strategy.entry("Short", strategy.short)`,
    },
    TradeRoom_Buy: {
      title: "TradeRoom Buy (EMA 9/15)",
      body: `// TradeRoom Buy: 9/15 EMA trend + bullish interaction + candle pattern
ema9 = ta.ema(close, 9)
ema15 = ta.ema(close, 15)
atr = ta.atr(14)
slope9 = (ema9 - ema9[3]) / atr
uptrend = ema9 > ema15 and slope9 > 0.3
ema_touch = low <= ema9 * 1.002 and close > ema9
bullish_candle = close > open and (close - low) > 1.5 * (high - close)
buy_signal = uptrend and ema_touch and bullish_candle
if buy_signal
    strategy.entry("Long", strategy.long)`,
    },
    TradeRoom_Sell: {
      title: "TradeRoom Sell (EMA 9/15)",
      body: `// TradeRoom Sell: 9/15 EMA downtrend + bearish interaction + candle pattern
ema9 = ta.ema(close, 9)
ema15 = ta.ema(close, 15)
atr = ta.atr(14)
slope9 = (ema9 - ema9[3]) / atr
downtrend = ema9 < ema15 and slope9 < -0.3
ema_touch = high >= ema9 * 0.998 and close < ema9
bearish_candle = close < open and (high - close) > 1.5 * (close - low)
sell_signal = downtrend and ema_touch and bearish_candle
if sell_signal
    strategy.entry("Short", strategy.short)`,
    },
  };

  const pineKey = strategyToPineKey(strategyName);
  const s = strategies[pineKey];
  if (!s) return null;

  return `//@version=5
strategy("${s.title}", overlay=true, initial_capital=100000, default_qty_type=strategy.percent_of_equity, default_qty_value=100, commission_type=strategy.commission.percent, commission_value=0.1)

// ─── Take Profit / Stop Loss ───
tp_pct = input.float(${tp}, "Take Profit %") / 100
sl_pct = input.float(${sl}, "Stop Loss %") / 100
max_bars = input.int(${maxHold}, "Max Hold Bars")

${s.body}

// ─── Exit Logic ───
if strategy.position_size > 0
    strategy.exit("TP/SL Long", "Long", profit=close * tp_pct / syminfo.mintick, loss=close * sl_pct / syminfo.mintick)
    if bar_index - strategy.opentrades.entry_bar_index(0) >= max_bars
        strategy.close("Long", comment="Max Hold")

if strategy.position_size < 0
    strategy.exit("TP/SL Short", "Short", profit=close * tp_pct / syminfo.mintick, loss=close * sl_pct / syminfo.mintick)
    if bar_index - strategy.opentrades.entry_bar_index(0) >= max_bars
        strategy.close("Short", comment="Max Hold")

// ─── Plots ───
plot(ta.ema(close, 9), "EMA 9", color=color.blue, linewidth=2)
plot(ta.ema(close, 15), "EMA 15", color=color.orange, linewidth=2)
`;
}

function downloadPineScript(strategyName, config) {
  const content = generatePineScript(strategyName, config);
  if (!content) return;
  const safeName = (strategyName || "strategy").replace(/[^a-zA-Z0-9_-]/g, "_");
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeName}_strategy.pine`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const MetricCard = ({ title, value, subtitle, color }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        minWidth: 140,
        background: "#ffffff",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <CardContent sx={{ textAlign: "center", py: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: color || "text.primary", mt: 0.5 }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

function TabPanel({ children, value, index, ...rest }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...rest}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const BacktestPage = () => {
  const theme = useTheme();
  const [resultsTab, setResultsTab] = useState(0);

  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [period, setPeriod] = useState(3);
  const [takeProfit, setTakeProfit] = useState(10);
  const [stopLoss, setStopLoss] = useState(5);
  const [maxHoldDays, setMaxHoldDays] = useState(20);
  const [strategies, setStrategies] = useState(STRATEGY_OPTIONS.map((s) => s.value));
  const [initialCapital, setInitialCapital] = useState(100000);
  const [mode, setMode] = useState("normal");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("nifty_50");
  const [loadingSymbols, setLoadingSymbols] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [renameDialog, setRenameDialog] = useState({ open: false, id: null, name: "" });
  const [tradeDetail, setTradeDetail] = useState(null);
  const [pineExportStrategy, setPineExportStrategy] = useState("");

  const presetsMap = useMemo(() => {
    const map = {};
    presets.forEach((p) => { map[p.key] = p; });
    return map;
  }, [presets]);

  const runStrategies = useMemo(() => {
    if (!result?.by_strategy) return [];
    return Object.keys(result.by_strategy);
  }, [result]);

  const pineExportOptions = useMemo(() => {
    const opts = runStrategies.filter((s) => generatePineScript(s, result?.config));
    return opts;
  }, [runStrategies, result?.config]);

  const loadHistory = useCallback(() => {
    getBacktestHistory().then((data) => {
      if (data && Array.isArray(data)) setHistory(data);
    });
  }, []);

  useEffect(() => {
    getIndexPresets().then((data) => {
      if (data && Array.isArray(data) && data.length > 0) setPresets(data);
    });
    getAvailableSymbols().then((syms) => {
      if (syms && syms.length > 0) setAvailableSymbols(syms);
    });
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!selectedPreset || selectedPreset === "custom") return;
    setLoadingSymbols(true);
    getIndexSymbols(selectedPreset).then((syms) => {
      if (syms && syms.length > 0) {
        setSelectedSymbols(syms);
        setSelectAll(true);
      }
      setLoadingSymbols(false);
    });
  }, [selectedPreset]);

  useEffect(() => {
    if (pineExportOptions.length > 0 && !pineExportStrategy) {
      setPineExportStrategy(pineExportOptions[0]);
    }
  }, [pineExportOptions, pineExportStrategy]);

  const handleRunBacktest = (forceRerun = false) => {
    setLoading(true);
    setError(null);
    setResult(null);

    const allStrategiesSelected = strategies.length === STRATEGY_OPTIONS.length;
    const params = {
      period,
      take_profit: takeProfit,
      stop_loss: stopLoss,
      max_hold_days: maxHoldDays,
      strategies: allStrategiesSelected ? "all" : strategies.join(","),
      initial_capital: initialCapital,
      mode,
    };

    if (forceRerun) params.force = "true";

    if (selectedPreset && selectedPreset !== "custom") {
      params.preset = selectedPreset;
      const presetCount = presetsMap[selectedPreset]?.count || 0;
      if (!selectAll && selectedSymbols.length !== presetCount) {
        params.symbols = selectedSymbols.join(",");
        delete params.preset;
      }
    } else {
      params.symbols = selectedSymbols.join(",");
    }

    getBacktestData(params)
      .then((data) => {
        if (data && data.error) {
          setError(data.error);
        } else {
          setResult(data);
          setResultsTab(0);
          loadHistory();
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to run backtest. Check if the backend is running.");
      })
      .finally(() => setLoading(false));
  };

  const handleLoadSavedResult = (runId) => {
    setLoading(true);
    setError(null);
    setHistoryOpen(false);
    getBacktestHistoryItem(runId)
      .then((data) => {
        if (data && !data.error) {
          setResult(data);
          setResultsTab(0);
          if (data.config) {
            if (data.config.initial_capital) setInitialCapital(data.config.initial_capital);
            if (data.config.mode) setMode(data.config.mode);
            if (data.config.period_years) setPeriod(data.config.period_years);
            if (data.config.take_profit) setTakeProfit(data.config.take_profit);
            if (data.config.stop_loss) setStopLoss(data.config.stop_loss);
            if (data.config.max_hold_days) setMaxHoldDays(data.config.max_hold_days);
          }
        } else {
          setError("Failed to load saved result.");
        }
      })
      .catch(() => setError("Failed to load saved result."))
      .finally(() => setLoading(false));
  };

  const handleDeleteRun = (runId) => {
    deleteBacktestRun(runId).then(() => loadHistory());
  };

  const handleRename = () => {
    if (renameDialog.id && renameDialog.name) {
      renameBacktestRun(renameDialog.id, renameDialog.name).then(() => {
        loadHistory();
        setRenameDialog({ open: false, id: null, name: "" });
      });
    }
  };

  const handleDownloadPine = () => {
    const strat = pineExportStrategy || pineExportOptions[0];
    if (strat && result?.config) downloadPineScript(strat, result.config);
  };

  const getEquityAtDate = useCallback((dateStr) => {
    if (!result?.equity_curve?.length) return null;
    const target = new Date(dateStr).getTime();
    let best = result.equity_curve[0];
    let bestDiff = Math.abs(new Date(best.date).getTime() - target);
    for (const p of result.equity_curve) {
      const d = new Date(p.date).getTime();
      const diff = Math.abs(d - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = p;
      }
    }
    return best?.equity ?? null;
  }, [result]);

  const equityChartOptions = useMemo(() => {
    if (!result || !result.equity_curve || result.equity_curve.length === 0) return null;
    const equityData = result.equity_curve.map((p) => [new Date(p.date).getTime(), p.equity]);
    const trades = result.trades || [];

    const entryLong = [];
    const entryShort = [];
    const exits = [];

    trades.forEach((t) => {
      const entryEquity = getEquityAtDate(t.entry_date);
      const exitEquity = getEquityAtDate(t.exit_date);
      if (entryEquity != null) {
        const ts = new Date(t.entry_date).getTime();
        if ((t.direction || "").toLowerCase() === "long") {
          entryLong.push({ x: ts, y: entryEquity, trade: t });
        } else {
          entryShort.push({ x: ts, y: entryEquity, trade: t });
        }
      }
      if (exitEquity != null) {
        exits.push({ x: new Date(t.exit_date).getTime(), y: exitEquity, trade: t });
      }
    });

    const series = [
      {
        name: "Equity",
        type: "area",
        data: equityData,
        color: COLORS.green,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[0, "rgba(46,125,50,0.35)"], [1, "rgba(46,125,50,0.02)"]],
        },
        threshold: null,
      },
    ];

    if (entryLong.length > 0) {
      series.push({
        name: "Long Entry",
        type: "scatter",
        data: entryLong.map((p) => ({ x: p.x, y: p.y, trade: p.trade })),
        marker: { symbol: "triangle", fillColor: COLORS.green, lineColor: COLORS.green, lineWidth: 1, size: 10 },
        tooltip: {
          pointFormatter: function () {
            const t = this.point.trade;
            if (!t) return "";
            return `<b>${t.symbol}</b> ${t.signal_type}<br/>Long @ ${t.entry_price}<br/>P&L: ${(t.pnl_pct || 0).toFixed(2)}%`;
          },
        },
      });
    }
    if (entryShort.length > 0) {
      series.push({
        name: "Short Entry",
        type: "scatter",
        data: entryShort.map((p) => ({ x: p.x, y: p.y, trade: p.trade })),
        marker: { symbol: "triangle-down", fillColor: COLORS.red, lineColor: COLORS.red, lineWidth: 1, size: 10 },
        tooltip: {
          pointFormatter: function () {
            const t = this.point.trade;
            if (!t) return "";
            return `<b>${t.symbol}</b> ${t.signal_type}<br/>Short @ ${t.entry_price}<br/>P&L: ${(t.pnl_pct || 0).toFixed(2)}%`;
          },
        },
      });
    }
    if (exits.length > 0) {
      series.push({
        name: "Exit",
        type: "scatter",
        data: exits.map((p) => ({ x: p.x, y: p.y, trade: p.trade })),
        marker: { symbol: "square", fillColor: COLORS.blue, lineColor: COLORS.blue, lineWidth: 1, size: 8 },
        tooltip: {
          pointFormatter: function () {
            const t = this.point.trade;
            if (!t) return "";
            return `<b>${t.symbol}</b><br/>Exit @ ${t.exit_price}<br/>P&L: ${(t.pnl_pct || 0).toFixed(2)}%`;
          },
        },
      });
    }

    return {
      chart: { type: "area", backgroundColor: "#ffffff", height: 420, border: "1px solid #e0e0e0" },
      title: { text: "Equity Curve with Trade Markers", style: { color: theme.palette.text.primary, fontSize: "16px" } },
      xAxis: { type: "datetime", labels: { style: { color: theme.palette.text.secondary } } },
      yAxis: {
        title: { text: "Portfolio Value", style: { color: theme.palette.text.secondary } },
        labels: { style: { color: theme.palette.text.secondary } },
        gridLineColor: "#e8e8e8",
      },
      legend: { itemStyle: { color: theme.palette.text.primary } },
      series,
      tooltip: { shared: false, valuePrefix: "₹", valueDecimals: 0 },
      credits: { enabled: false },
    };
  }, [result, theme, getEquityAtDate]);

  const pnlBarChartOptions = useMemo(() => {
    const trades = (result?.trades || []).slice().sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date));
    if (trades.length === 0) return null;

    const categories = trades.map((t, i) => `${t.symbol} #${i + 1}`);
    const data = trades.map((t) => ({
      y: t.pnl_pct ?? 0,
      color: (t.pnl_pct ?? 0) >= 0 ? COLORS.green : COLORS.red,
    }));

    return {
      chart: { type: "column", backgroundColor: "#ffffff", height: 320, border: "1px solid #e0e0e0" },
      title: { text: "Trade P&L % by Entry Date", style: { color: theme.palette.text.primary, fontSize: "14px" } },
      xAxis: { categories, labels: { style: { fontSize: "10px", color: theme.palette.text.secondary } } },
      yAxis: {
        title: { text: "P&L %", style: { color: theme.palette.text.secondary } },
        labels: { style: { color: theme.palette.text.secondary } },
        gridLineColor: "#e8e8e8",
      },
      legend: { enabled: false },
      series: [{ name: "P&L %", data }],
      tooltip: {
        pointFormatter: function () {
          const t = trades[this.point.index];
          return `<b>${t?.symbol}</b> ${t?.signal_type}<br/>Entry: ${t?.entry_date}<br/>P&L: ${(this.y || 0).toFixed(2)}%`;
        },
      },
      credits: { enabled: false },
    };
  }, [result, theme]);

  const overviewEquityChartOptions = useMemo(() => {
    if (!result || !result.equity_curve || result.equity_curve.length === 0) return null;
    const equityData = result.equity_curve.map((p) => [new Date(p.date).getTime(), p.equity]);
    return {
      chart: { type: "area", backgroundColor: "#ffffff", height: 360, border: "1px solid #e0e0e0" },
      title: { text: "Equity Curve", style: { color: theme.palette.text.primary, fontSize: "16px" } },
      xAxis: { type: "datetime", labels: { style: { color: theme.palette.text.secondary } } },
      yAxis: {
        title: { text: "Portfolio Value", style: { color: theme.palette.text.secondary } },
        labels: { style: { color: theme.palette.text.secondary } },
        gridLineColor: "#e8e8e8",
      },
      legend: { enabled: false },
      series: [{
        name: "Equity",
        type: "area",
        data: equityData,
        color: COLORS.green,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[0, "rgba(46,125,50,0.4)"], [1, "rgba(46,125,50,0.0)"]],
        },
        threshold: null,
      }],
      tooltip: { valuePrefix: "₹", valueDecimals: 0 },
      credits: { enabled: false },
    };
  }, [result, theme]);

  const strategyColumns = useMemo(() => [
    { accessorKey: "strategy", header: "Strategy", size: 180 },
    { accessorKey: "total_trades", header: "Trades", size: 80 },
    { accessorKey: "win_rate", header: "Win Rate", size: 100, Cell: ({ cell }) => `${((cell.getValue() ?? 0) * 100).toFixed(1)}%` },
    { accessorKey: "avg_win", header: "Avg Win %", size: 100, Cell: ({ cell }) => <span style={{ color: COLORS.green }}>+{(cell.getValue() ?? 0)}%</span> },
    { accessorKey: "avg_loss", header: "Avg Loss %", size: 100, Cell: ({ cell }) => <span style={{ color: COLORS.red }}>{(cell.getValue() ?? 0)}%</span> },
    { accessorKey: "profit_factor", header: "Profit Factor", size: 120 },
    { accessorKey: "total_return", header: "Return %", size: 110, Cell: ({ cell }) => { const v = cell.getValue() ?? 0; return <span style={{ color: v >= 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}>{v >= 0 ? "+" : ""}{v}%</span>; } },
    { accessorKey: "sharpe_ratio", header: "Sharpe", size: 90 },
    { accessorKey: "max_drawdown", header: "Max DD %", size: 100, Cell: ({ cell }) => <span style={{ color: COLORS.red }}>{(cell.getValue() ?? 0)}%</span> },
    { accessorKey: "avg_hold_days", header: "Avg Hold Days", size: 130 },
  ], []);

  const strategyData = useMemo(() => {
    if (!result || !result.by_strategy) return [];
    return Object.entries(result.by_strategy).map(([key, val]) => ({ strategy: key, ...val }));
  }, [result]);

  const strategyTable = useMaterialReactTable({
    columns: strategyColumns,
    data: strategyData,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableStickyHeader: true,
    enablePagination: false,
    initialState: { density: "compact" },
    muiTablePaperProps: { sx: { border: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" } },
  });

  const tradeColumns = useMemo(() => [
    { accessorKey: "symbol", header: "Symbol", size: 130 },
    { accessorKey: "signal_type", header: "Signal", size: 180 },
    { accessorKey: "direction", header: "Direction", size: 90 },
    { accessorKey: "entry_date", header: "Entry Date", size: 120 },
    { accessorKey: "entry_price", header: "Entry Price", size: 110 },
    { accessorKey: "exit_date", header: "Exit Date", size: 120 },
    { accessorKey: "exit_price", header: "Exit Price", size: 110 },
    { accessorKey: "pnl_pct", header: "P&L %", size: 100, Cell: ({ cell }) => { const v = cell.getValue() ?? 0; return <span style={{ color: v >= 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}>{v >= 0 ? "+" : ""}{v}%</span>; } },
    { accessorKey: "pnl_amount", header: "P&L Amount", size: 130, Cell: ({ cell }) => { const v = cell.getValue(); if (v === null || v === undefined) return "-"; return <span style={{ color: v >= 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}>{v >= 0 ? "+" : ""}{formatCurrency(v)}</span>; } },
    { accessorKey: "hold_days", header: "Hold Days", size: 100 },
    { accessorKey: "exit_reason", header: "Exit Reason", size: 140, Cell: ({ cell }) => { const v = cell.getValue(); const colorMap = { take_profit: "success", stop_loss: "error", max_hold: "warning", opposite_signal: "info", end_of_data: "default" }; return <Chip label={v || "-"} color={colorMap[v] || "default"} size="small" />; } },
  ], []);

  const tradeData = useMemo(() => result?.trades || [], [result]);

  const tradeTable = useMaterialReactTable({
    columns: tradeColumns,
    data: tradeData,
    enableFullScreenToggle: false,
    enableStickyHeader: true,
    enableColumnPinning: true,
    initialState: { density: "compact", sorting: [{ id: "entry_date", desc: true }], columnPinning: { left: ["symbol"] }, pagination: { pageSize: 25, pageIndex: 0 } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => setTradeDetail(row.original),
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: { sx: { border: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" } },
  });

  const s = result?.summary;

  const computeSLTP = (trade) => {
    const tp = result?.config?.take_profit ?? 10;
    const sl = result?.config?.stop_loss ?? 5;
    const ep = trade?.entry_price ?? 0;
    const isLong = (trade?.direction || "").toLowerCase() === "long";
    const slLevel = isLong ? ep * (1 - sl / 100) : ep * (1 + sl / 100);
    const tpLevel = isLong ? ep * (1 + tp / 100) : ep * (1 - tp / 100);
    return { slLevel, tpLevel };
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", background: "#f5f5f5", p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>Backtesting Engine</Typography>
          <Tooltip title="Saved Results">
            <IconButton onClick={() => { loadHistory(); setHistoryOpen(true); }} color="primary" sx={{ bgcolor: "#fff", border: "1px solid #e0e0e0", "&:hover": { bgcolor: "#f5f5f5" } }}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          {result?.cached && (
            <Chip icon={<CachedIcon />} label="Cached Result" color="info" variant="outlined" size="small" />
          )}
        </Box>
        {result && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {pineExportOptions.length > 0 && (
              <>
                {pineExportOptions.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Strategy to Export</InputLabel>
                    <Select
                      value={pineExportStrategy || pineExportOptions[0]}
                      onChange={(e) => setPineExportStrategy(e.target.value)}
                      label="Strategy to Export"
                    >
                      {pineExportOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPine}
                  sx={{ borderColor: COLORS.blue, color: COLORS.blue }}
                >
                  Download Pine Script
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Configuration Panel */}
      <Card sx={{ mb: 3, p: 2, background: "#ffffff", border: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Configuration</Typography>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Universe Selection</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {presets.map((preset) => (
            <Chip
              key={preset.key}
              label={`${preset.label}${preset.count ? ` (${preset.count})` : ""}`}
              onClick={() => { setSelectedPreset(preset.key); if (preset.key === "custom") { setSelectedSymbols([]); setSelectAll(false); } }}
              color={selectedPreset === preset.key ? "primary" : "default"}
              variant={selectedPreset === preset.key ? "filled" : "outlined"}
              sx={{ fontWeight: selectedPreset === preset.key ? 700 : 400, cursor: "pointer" }}
            />
          ))}
        </Box>
        {selectedPreset && selectedPreset !== "custom" && presetsMap[selectedPreset] && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
            {presetsMap[selectedPreset].description} — {selectedSymbols.length} symbols loaded
          </Typography>
        )}

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={9}>
            <Autocomplete
              multiple
              freeSolo
              options={selectedPreset === "custom" ? availableSymbols : selectedSymbols.length > 0 ? selectedSymbols : availableSymbols}
              value={selectedSymbols}
              onChange={(event, newValue) => { setSelectedSymbols(newValue); if (selectedPreset !== "custom") { const presetCount = presetsMap[selectedPreset]?.count || 0; setSelectAll(newValue.length >= presetCount); } }}
              disableCloseOnSelect
              loading={loadingSymbols}
              renderTags={(value, getTagProps) => value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} key={option} />)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Symbols"
                  size="small"
                  placeholder={selectedPreset === "custom" ? "Search or type a Yahoo Finance ticker..." : "Preset loaded — remove symbols or type to add more..."}
                  helperText={`${selectedSymbols.length} symbol${selectedSymbols.length !== 1 ? "s" : ""} selected${selectedPreset !== "custom" ? ` from ${presetsMap[selectedPreset]?.label || selectedPreset}` : ""}`}
                />
              )}
              sx={{ "& .MuiAutocomplete-tag": { maxWidth: 120 } }}
              limitTags={8}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={<Checkbox checked={selectAll} onChange={(e) => { const checked = e.target.checked; setSelectAll(checked); if (checked && selectedPreset && selectedPreset !== "custom") { setLoadingSymbols(true); getIndexSymbols(selectedPreset).then((syms) => { if (syms && syms.length > 0) setSelectedSymbols(syms); setLoadingSymbols(false); }); } else if (!checked) { setSelectedSymbols([]); } }} />}
              label="Select All"
              sx={{ mr: 2 }}
            />
            <Button variant="outlined" size="small" onClick={() => { setSelectedSymbols([]); setSelectAll(false); }}>Clear</Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="Initial Capital"
              type="number"
              value={initialCapital}
              onChange={(e) => setInitialCapital(Number(e.target.value))}
              size="small"
              InputProps={{ inputProps: { min: 1000, step: 10000 } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Mode</Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, newMode) => { if (newMode) setMode(newMode); }}
              size="small"
              fullWidth
            >
              <ToggleButton value="normal">Normal</ToggleButton>
              <ToggleButton value="compounding">Compounding</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth select label="Period (years)" value={period} onChange={(e) => setPeriod(Number(e.target.value))} size="small">
              {[1, 2, 3, 5].map((y) => <MenuItem key={y} value={y}>{y}y</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth label="Take Profit %" type="number" value={takeProfit} onChange={(e) => setTakeProfit(Number(e.target.value))} size="small" />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth label="Stop Loss %" type="number" value={stopLoss} onChange={(e) => setStopLoss(Number(e.target.value))} size="small" />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth label="Max Hold Days" type="number" value={maxHoldDays} onChange={(e) => setMaxHoldDays(Number(e.target.value))} size="small" />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="strategy-select-label">Strategies</InputLabel>
              <Select
                labelId="strategy-select-label"
                multiple
                value={strategies}
                onChange={(e) => setStrategies(e.target.value)}
                input={<OutlinedInput label="Strategies" />}
                renderValue={(selected) => selected.map((val) => STRATEGY_OPTIONS.find((s) => s.value === val)?.label || val).join(", ")}
              >
                {STRATEGY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={strategies.includes(opt.value)} sx={{ mr: 1 }} />
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              variant="contained"
              onClick={() => handleRunBacktest(false)}
              disabled={loading || strategies.length === 0 || selectedSymbols.length === 0}
              fullWidth
              sx={{ height: 40, bgcolor: COLORS.blue }}
            >
              {loading ? "Running..." : "Run Backtest"}
            </Button>
          </Grid>
          {result?.cached && (
            <Grid item xs={6} md={2}>
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                startIcon={<ReplayIcon />}
                onClick={() => handleRunBacktest(true)}
                disabled={loading}
                sx={{ height: 40 }}
              >
                Re-run Fresh
              </Button>
            </Grid>
          )}
        </Grid>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {mode === "normal" ? "Normal: Each trade uses full initial capital." : "Compounding: Profits/losses carry forward."}
        </Typography>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={60} sx={{ color: COLORS.blue }} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Running backtest across {selectedSymbols.length} symbol{selectedSymbols.length !== 1 ? "s" : ""}...
          </Typography>
        </Box>
      )}

      {/* Results */}
      {result && s && (
        <Card sx={{ background: "#ffffff", border: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <Tabs value={resultsTab} onChange={(e, v) => setResultsTab(v)} sx={{ borderBottom: "1px solid #e0e0e0", px: 2 }}>
            <Tab label="Overview" />
            <Tab label="Strategy Comparison" />
            <Tab label="Trade Log" />
            <Tab label="Trade Chart" />
          </Tabs>

          <TabPanel value={resultsTab} index={0}>
            <Box sx={{ px: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Capital Summary</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Initial Capital" value={formatCurrency(s.initial_capital)} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Final Equity" value={formatCurrency(s.final_equity)} color={s.final_equity >= s.initial_capital ? COLORS.green : COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Total P&L" value={formatCurrency(s.total_pnl_amount)} color={s.total_pnl_amount >= 0 ? COLORS.green : COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Return %" value={`${s.total_return >= 0 ? "+" : ""}${s.total_return}%`} color={s.total_return >= 0 ? COLORS.green : COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Mode" value={s.mode === "compounding" ? "Compound" : "Normal"} color={COLORS.blue} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Total Trades" value={s.total_trades} />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Performance Metrics</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Win Rate" value={`${((s.win_rate ?? 0) * 100).toFixed(1)}%`} color={(s.win_rate ?? 0) >= 0.5 ? COLORS.green : COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Profit Factor" value={s.profit_factor ?? "-"} color={(s.profit_factor ?? 0) >= 1 ? COLORS.green : COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Max Drawdown" value={`${s.max_drawdown ?? 0}%`} color={COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Sharpe Ratio" value={s.sharpe_ratio ?? "-"} color={(s.sharpe_ratio ?? 0) >= 1 ? COLORS.green : COLORS.orange} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Avg Win" value={`+${s.avg_win ?? 0}%`} color={COLORS.green} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Avg Loss" value={`${s.avg_loss ?? 0}%`} color={COLORS.red} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Best Trade" value={`+${s.best_trade ?? 0}%`} color={COLORS.green} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard title="Avg Hold Days" value={`${s.avg_hold_days ?? 0} days`} />
                </Grid>
              </Grid>

              {overviewEquityChartOptions && (
                <Box sx={{ mt: 3 }}>
                  <HighchartsReact highcharts={Highcharts} options={overviewEquityChartOptions} />
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={resultsTab} index={1}>
            <Box sx={{ px: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Performance by Strategy</Typography>
              <MaterialReactTable table={strategyTable} />
            </Box>
          </TabPanel>

          <TabPanel value={resultsTab} index={2}>
            <Box sx={{ px: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Trade Log ({result.trades?.length || 0} trades) — Click a row for details</Typography>
              <MaterialReactTable table={tradeTable} />
            </Box>
          </TabPanel>

          <TabPanel value={resultsTab} index={3}>
            <Box sx={{ px: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Equity Curve with Trade Markers</Typography>
              {equityChartOptions && <HighchartsReact highcharts={Highcharts} options={equityChartOptions} />}
              <Typography variant="subtitle1" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>Trade P&L Distribution</Typography>
              {pnlBarChartOptions && <HighchartsReact highcharts={Highcharts} options={pnlBarChartOptions} />}
            </Box>
          </TabPanel>
        </Card>
      )}

      {/* Trade Detail Dialog */}
      <Dialog open={!!tradeDetail} onClose={() => setTradeDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Trade Details</DialogTitle>
        <DialogContent>
          {tradeDetail && (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Symbol</TableCell><TableCell>{tradeDetail.symbol}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Signal Type</TableCell><TableCell>{tradeDetail.signal_type}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Direction</TableCell><TableCell>{tradeDetail.direction}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Entry Date</TableCell><TableCell>{tradeDetail.entry_date}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Entry Price</TableCell><TableCell>{formatCurrency(tradeDetail.entry_price)}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Exit Date</TableCell><TableCell>{tradeDetail.exit_date}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Exit Price</TableCell><TableCell>{formatCurrency(tradeDetail.exit_price)}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>P&L %</TableCell><TableCell><span style={{ color: (tradeDetail.pnl_pct ?? 0) >= 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}>{(tradeDetail.pnl_pct ?? 0) >= 0 ? "+" : ""}{(tradeDetail.pnl_pct ?? 0)}%</span></TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>P&L Amount</TableCell><TableCell><span style={{ color: (tradeDetail.pnl_amount ?? 0) >= 0 ? COLORS.green : COLORS.red }}>{formatCurrency(tradeDetail.pnl_amount)}</span></TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Hold Days</TableCell><TableCell>{tradeDetail.hold_days}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Exit Reason</TableCell><TableCell><Chip label={tradeDetail.exit_reason || "-"} size="small" /></TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Stop Loss Level</TableCell><TableCell>{formatCurrency(computeSLTP(tradeDetail).slLevel)}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Take Profit Level</TableCell><TableCell>{formatCurrency(computeSLTP(tradeDetail).tpLevel)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTradeDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Saved Results Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Saved Backtest Results</DialogTitle>
        <DialogContent>
          {history.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No saved results yet. Run a backtest to save results automatically.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Preset</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="right">Capital</TableCell>
                    <TableCell align="right">Trades</TableCell>
                    <TableCell align="right">Return %</TableCell>
                    <TableCell align="right">Final Equity</TableCell>
                    <TableCell align="right">Max DD %</TableCell>
                    <TableCell align="right">Win Rate</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((run) => (
                    <TableRow key={run.id} hover sx={{ cursor: "pointer" }} onClick={() => handleLoadSavedResult(run.id)}>
                      <TableCell>{run.name || `Run #${run.id}`}</TableCell>
                      <TableCell>{run.preset || "custom"}</TableCell>
                      <TableCell><Chip label={run.mode || "normal"} size="small" /></TableCell>
                      <TableCell align="right">{formatCurrency(run.initial_capital)}</TableCell>
                      <TableCell align="right">{run.total_trades}</TableCell>
                      <TableCell align="right">
                        <span style={{ color: (run.total_return_pct ?? 0) >= 0 ? COLORS.green : COLORS.red }}>
                          {(run.total_return_pct ?? 0) >= 0 ? "+" : ""}{Number(run.total_return_pct ?? 0).toFixed(2)}%
                        </span>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(run.final_equity)}</TableCell>
                      <TableCell align="right"><span style={{ color: COLORS.red }}>{Number(run.max_drawdown ?? 0).toFixed(2)}%</span></TableCell>
                      <TableCell align="right">{(Number(run.win_rate ?? 0) * 100).toFixed(1)}%</TableCell>
                      <TableCell>{run.created_at ? new Date(run.created_at).toLocaleDateString() : "-"}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Rename">
                          <IconButton size="small" onClick={() => setRenameDialog({ open: true, id: run.id, name: run.name || "" })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteRun(run.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onClose={() => setRenameDialog({ open: false, id: null, name: "" })}>
        <DialogTitle>Rename Backtest Run</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" value={renameDialog.name} onChange={(e) => setRenameDialog({ ...renameDialog, name: e.target.value })} sx={{ mt: 1 }} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, id: null, name: "" })}>Cancel</Button>
          <Button onClick={handleRename} variant="contained" sx={{ bgcolor: COLORS.blue }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BacktestPage;
